import React, { useState } from 'react';
import { Eye, TrendingUp, TrendingDown, Award, ArrowLeftRight, Info } from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Tooltip,
} from 'recharts';

const DELTA_THRESHOLD = 10;
const MAX_HEATMAP_ELEMENTS = 5;
const MAX_DRIVER_ELEMENTS = 5;
const MAX_BLOCKER_ELEMENTS = 5;
const MAX_MIXED_ELEMENTS = 5;
const MIXED_MIN_TOTAL_MENTION = 30;

interface ElementReaction {
  theme: string;
  mention_percentage?: number;
  quotes: string[];
}

interface ElementAnalysis {
  name: string;
  positive_reactions: ElementReaction[];
  negative_reactions: ElementReaction[];
  key_insight: string;
}

interface DimensionScore {
  dimension: string;
  score: number;
  element_count: number;
}

interface BoundingBox {
  element_name: string;
  x_percent: number;
  y_percent: number;
  width_percent: number;
  height_percent: number;
}

interface FrontAnalysisData {
  overall_score: number;
  dimension_scores: DimensionScore[];
  purchase_intent_distribution: { wont_buy: number; neutral: number; will_buy: number };
  elements: ElementAnalysis[];
  bounding_boxes: BoundingBox[];
  raw_rationale_count: number;
  benchmark_will_buy?: number;
  percentile_rank?: number;
}

interface FrontAnalysisSectionProps {
  data: FrontAnalysisData;
  frontImageUrl?: string;
  buildImageUrl: (url: string) => string;
}

function getTopMention(el: ElementAnalysis, type: 'positive' | 'negative'): number {
  const reactions = type === 'positive' ? el.positive_reactions : el.negative_reactions;
  return Math.max(0, ...reactions.map(r => r.mention_percentage || 0));
}

const HeroScore: React.FC<{
  score: number;
  benchmark_will_buy?: number;
}> = ({ score, benchmark_will_buy }) => {
  const delta = benchmark_will_buy !== undefined ? score - benchmark_will_buy : 0;
  const isAboveNorm = delta >= 0;
  const pct = Math.min(100, Math.max(0, score));
  const radius = 54;
  const stroke = 8;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference * (1 - pct / 100);

  const ringColor = score >= 70 ? '#10B981' : score >= 50 ? '#F97316' : score >= 30 ? '#F59E0B' : '#EF4444';
  const trackColor = '#F1F5F9';

  return (
    <div className="flex flex-col items-center py-6">
      <p className="text-sm font-semibold text-[#1C1C1E] mb-3">Front Performance Score</p>
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={radius} fill="none" stroke={trackColor} strokeWidth={stroke} />
          <circle
            cx="64" cy="64" r={radius} fill="none"
            stroke={ringColor} strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashoffset}
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-[#1C1C1E]">{Math.round(score)}</span>
          <span className="text-[10px] text-[#9CA3AF] -mt-0.5">/ 100</span>
        </div>
      </div>

      {benchmark_will_buy !== undefined && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-[#6B7280] flex items-center gap-1">
            Norm: {benchmark_will_buy.toFixed(0)}
            <span className="relative group/info">
              <Info className="w-3 h-3 text-[#9CA3AF] cursor-help" />
              <span className="hidden group-hover/info:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#1C1C1E] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-50">
                Average baseline front performance score across CPG categories
              </span>
            </span>
          </span>
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${isAboveNorm ? 'text-emerald-700 bg-emerald-50' : 'text-orange-700 bg-orange-50'}`}>
            {isAboveNorm ? '+' : ''}{delta.toFixed(0)} points
          </span>
        </div>
      )}
    </div>
  );
};

const AttentionHeatmap: React.FC<{
  imageUrl: string;
  boxes: BoundingBox[];
  elements: ElementAnalysis[];
  buildImageUrl: (url: string) => string;
}> = ({ imageUrl, boxes, elements, buildImageUrl }) => {
  if (!boxes.length) return null;

  const elementMap = new Map(elements.map(el => [el.name, el]));

  // Normalize pixel values to percentages if LLM returned pixels
  const maxVal = Math.max(...boxes.map(b => b.x_percent + b.width_percent));
  const normalize = maxVal > 100;
  const scale = normalize ? 100 / maxVal : 1;

  const boxData = boxes
    .filter(b => b.width_percent > 0 || b.height_percent > 0)
    .map(b => {
      const nb = normalize ? {
        ...b,
        x_percent: b.x_percent * scale,
        y_percent: b.y_percent * scale,
        width_percent: b.width_percent * scale,
        height_percent: b.height_percent * scale,
      } : b;
      return nb;
    })
    .map(b => {
      const el = elementMap.get(b.element_name);
      const posPct = el ? Math.max(0, ...el.positive_reactions.map(r => r.mention_percentage || 0)) : 0;
      const negPct = el ? Math.max(0, ...el.negative_reactions.map(r => r.mention_percentage || 0)) : 0;
      const topMention = Math.min(100, posPct + negPct);
      return { ...b, topMention, el };
    })
    .sort((a, b) => b.topMention - a.topMention)
    .slice(0, MAX_HEATMAP_ELEMENTS);

  const boxColors = ['#07E5D1', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div>
      <h3 className="text-sm font-semibold text-[#1C1C1E] mb-4 flex items-center justify-center gap-2">
        <Eye className="w-4 h-4 text-[#07E5D1]" />
        Key Elements Impacting Purchase Intent
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Image with outlined boxes */}
        <div className="relative inline-block w-full print-keep-overflow">
          <img
            src={buildImageUrl(imageUrl)}
            alt="Front packaging with element annotations"
            className="w-full h-auto rounded-lg"
          />
          {[...boxData]
            .sort((a, b) => (b.width_percent * b.height_percent) - (a.width_percent * a.height_percent))
            .map((box) => {
            const rank = boxData.indexOf(box);
            const color = boxColors[rank % boxColors.length];
            return (
              <div
                key={`box-${rank}`}
                className="absolute rounded"
                style={{
                  left: `${box.x_percent}%`,
                  top: `${box.y_percent}%`,
                  width: `${box.width_percent}%`,
                  bottom: `${100 - box.y_percent - box.height_percent}%`,
                  border: `2px solid ${color}`,
                  zIndex: rank + 1,
                }}
              >
                <span
                  className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                  style={{ backgroundColor: color }}
                >
                  {rank + 1}
                </span>
              </div>
            );
          })}
        </div>

        {/* Ranked list */}
        <div className="space-y-2 flex flex-col justify-center">
          {boxData.map((box, idx) => {
            const color = boxColors[idx % boxColors.length];
            return (
              <div key={idx} className="flex items-start gap-2.5">
                <span
                  className="flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: color }}
                >
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#1C1C1E] font-semibold">{box.element_name}</span>
                    <span className="text-[10px] text-[#6B7280]">{box.topMention}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 print-bar">
                    <div
                      className="h-full rounded-full print-bar"
                      style={{ width: `${Math.max(5, box.topMention)}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const RankedDrivers: React.FC<{ elements: ElementAnalysis[] }> = ({ elements }) => {
  const drivers = elements
    .filter(el => {
      const pos = getTopMention(el, 'positive');
      const neg = getTopMention(el, 'negative');
      return pos - neg >= DELTA_THRESHOLD;
    })
    .sort((a, b) => getTopMention(b, 'positive') - getTopMention(a, 'positive'))
    .slice(0, MAX_DRIVER_ELEMENTS);

  return (
    <div>
      <h3 className="text-sm font-semibold text-[#1C1C1E] mb-3 flex items-center">
        <TrendingUp className="w-4 h-4 mr-2 text-emerald-600" />
        Top Drivers
      </h3>
      {!drivers.length && (
        <p className="text-xs text-[#9CA3AF] italic">No significant drivers detected</p>
      )}
      <div className="space-y-3">
        {drivers.map((el, idx) => {
          const pct = getTopMention(el, 'positive');
          return (
            <div key={idx} className="border border-emerald-200 rounded-lg p-4 bg-emerald-50/30">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-sm text-[#1C1C1E] flex-1">{el.name}</h4>
                <span className="text-[10px] text-emerald-700 font-semibold whitespace-nowrap">{pct}%</span>
              </div>
              <div className="w-full h-1.5 bg-emerald-100 rounded-full mb-2 print-bar">
                <div className="h-full bg-emerald-500 rounded-full print-bar" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              {el.positive_reactions.map((r, rIdx) => (
                <div key={rIdx} className="mb-1.5">
                  <p className="text-xs text-[#374151] font-medium">{r.theme}</p>
                  {r.quotes.slice(0, 1).map((q, qIdx) => (
                    <p key={qIdx} className="text-xs text-[#6B7280] italic mt-0.5 pl-3 border-l-2 border-emerald-200">
                      &ldquo;{q}&rdquo;
                    </p>
                  ))}
                </div>
              ))}
              {el.key_insight && (
                <p className="text-xs text-[#374151] mt-1">
                  <span className="font-semibold text-[#07E5D1]">Insight:</span> {el.key_insight}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RankedBlockers: React.FC<{ elements: ElementAnalysis[] }> = ({ elements }) => {
  const blockers = elements
    .filter(el => {
      const pos = getTopMention(el, 'positive');
      const neg = getTopMention(el, 'negative');
      return neg - pos >= DELTA_THRESHOLD;
    })
    .sort((a, b) => getTopMention(b, 'negative') - getTopMention(a, 'negative'))
    .slice(0, MAX_BLOCKER_ELEMENTS);

  return (
    <div>
      <h3 className="text-sm font-semibold text-[#1C1C1E] mb-3 flex items-center">
        <TrendingDown className="w-4 h-4 mr-2 text-red-600" />
        Top Blockers
      </h3>
      {!blockers.length && (
        <p className="text-xs text-[#9CA3AF] italic">No significant blockers detected</p>
      )}
      <div className="space-y-3">
        {blockers.map((el, idx) => {
          const pct = getTopMention(el, 'negative');
          return (
            <div key={idx} className="border border-red-200 rounded-lg p-4 bg-red-50/30">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-sm text-[#1C1C1E] flex-1">{el.name}</h4>
                <span className="text-[10px] text-red-700 font-semibold whitespace-nowrap">{pct}%</span>
              </div>
              <div className="w-full h-1.5 bg-red-100 rounded-full mb-2 print-bar">
                <div className="h-full bg-red-500 rounded-full print-bar" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              {el.negative_reactions.map((r, rIdx) => (
                <div key={rIdx} className="mb-1.5">
                  <p className="text-xs text-[#374151] font-medium">{r.theme}</p>
                  {r.quotes.slice(0, 1).map((q, qIdx) => (
                    <p key={qIdx} className="text-xs text-[#6B7280] italic mt-0.5 pl-3 border-l-2 border-red-200">
                      &ldquo;{q}&rdquo;
                    </p>
                  ))}
                </div>
              ))}
              {el.key_insight && (
                <p className="text-xs text-[#374151] mt-1">
                  <span className="font-semibold text-[#07E5D1]">Insight:</span> {el.key_insight}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MixedElements: React.FC<{ elements: ElementAnalysis[] }> = ({ elements }) => {
  const mixed = elements
    .filter(el => {
      const pos = getTopMention(el, 'positive');
      const neg = getTopMention(el, 'negative');
      const total = pos + neg;
      return Math.abs(pos - neg) < DELTA_THRESHOLD && total >= MIXED_MIN_TOTAL_MENTION;
    })
    .sort((a, b) => {
      const totalA = getTopMention(a, 'positive') + getTopMention(a, 'negative');
      const totalB = getTopMention(b, 'positive') + getTopMention(b, 'negative');
      return totalB - totalA;
    })
    .slice(0, MAX_MIXED_ELEMENTS);

  if (!mixed.length) return null;

  return (
    <div>
      <h3 className="text-sm font-semibold text-[#1C1C1E] mb-3 flex items-center">
        <ArrowLeftRight className="w-4 h-4 mr-2 text-amber-500" />
        Mixed Reactions
      </h3>
      <div className="space-y-3">
        {mixed.map((el, idx) => {
          const pos = getTopMention(el, 'positive');
          const neg = getTopMention(el, 'negative');
          return (
            <div key={idx} className="border border-amber-200 rounded-lg p-4 bg-amber-50/30">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-sm text-[#1C1C1E] flex-1">{el.name}</h4>
                <span className="text-[10px] text-emerald-700 font-semibold">{pos}%</span>
                <span className="text-[10px] text-[#9CA3AF]">/</span>
                <span className="text-[10px] text-red-700 font-semibold">{neg}%</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full mb-2 flex overflow-hidden print-bar print-keep-overflow">
                <div className="h-full bg-emerald-500 print-bar" style={{ width: `${pos}%` }} />
                <div className="h-full bg-red-500 print-bar" style={{ width: `${neg}%` }} />
              </div>
              {el.positive_reactions.length > 0 && (
                <div className="mb-1.5">
                  <p className="text-xs text-emerald-700 font-medium">{el.positive_reactions[0].theme}</p>
                  {el.positive_reactions[0].quotes.slice(0, 1).map((q, qIdx) => (
                    <p key={qIdx} className="text-xs text-[#6B7280] italic mt-0.5 pl-3 border-l-2 border-emerald-200">
                      &ldquo;{q}&rdquo;
                    </p>
                  ))}
                </div>
              )}
              {el.negative_reactions.length > 0 && (
                <div className="mb-1.5">
                  <p className="text-xs text-red-700 font-medium">{el.negative_reactions[0].theme}</p>
                  {el.negative_reactions[0].quotes.slice(0, 1).map((q, qIdx) => (
                    <p key={qIdx} className="text-xs text-[#6B7280] italic mt-0.5 pl-3 border-l-2 border-red-200">
                      &ldquo;{q}&rdquo;
                    </p>
                  ))}
                </div>
              )}
              {el.key_insight && (
                <p className="text-xs text-[#374151] mt-1">
                  <span className="font-semibold text-[#07E5D1]">Insight:</span> {el.key_insight}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FrontAnalysisSection: React.FC<FrontAnalysisSectionProps> = ({
  data,
  frontImageUrl,
  buildImageUrl,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
        <Award className="w-5 h-5 text-[#07E5D1] mr-2" />
        <div>
          <h2 className="text-xl font-semibold text-[#1C1C1E]">Front Analysis</h2>
          <p className="text-xs text-[#6B7280] mt-1">Consumer reactions to the front view of the packaging</p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Hero Score */}
        <div className="flex justify-center">
          <HeroScore
            score={data.overall_score}
            benchmark_will_buy={data.benchmark_will_buy}
          />
        </div>

        {/* Attention Heatmap */}
        {frontImageUrl && data.bounding_boxes && data.bounding_boxes.length > 0 && (
          <div className="flex justify-center">
            <AttentionHeatmap
              imageUrl={frontImageUrl}
              boxes={data.bounding_boxes}
              elements={data.elements}
              buildImageUrl={buildImageUrl}
            />
          </div>
        )}

        {/* Ranked Drivers & Blockers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <RankedDrivers elements={data.elements} />
          <RankedBlockers elements={data.elements} />
        </div>

        {/* Mixed Reactions */}
        <MixedElements elements={data.elements} />
      </div>
    </div>
  );
};

export default FrontAnalysisSection;